import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';

// The three pieces of page state that live in the URL so a reload restores exactly what you were
// looking at: the selected workflow node, the author filter, and the open PR.
export const AUTHOR_ALL = 'all';
const AUTHOR_STORAGE_KEY = 'deli.prAuthorFilter';

export interface PrPageUrlState {
  stage: string | null;
  author: string; // a GitHub login or AUTHOR_ALL
  prNumber: number | null;
  setStage: (stage: string | null) => void;
  setAuthor: (author: string) => void;
  setPr: (prNumber: number | null) => void;
}

export function usePrPageUrlState(): PrPageUrlState {
  const [params, setParams] = useSearchParams();
  const { userProfile } = useConfig();

  const stage = params.get('stage');
  const prParam = params.get('pr');
  const prNumber = prParam ? parseInt(prParam, 10) : null;

  // Author precedence: an explicit URL value wins (so shared links are faithful), then the last
  // choice remembered in localStorage, then a sensible default of "just my PRs" (falling back to
  // "all" when we don't know the user's GitHub login yet).
  const author = useMemo(() => {
    const urlAuthor = params.get('author');
    if (urlAuthor) return urlAuthor;
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTHOR_STORAGE_KEY) : null;
    if (stored) return stored;
    return userProfile?.githubUsername || AUTHOR_ALL;
  }, [params, userProfile?.githubUsername]);

  const update = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      // Build from the current params (read on each render) rather than the functional updater form,
      // for compatibility with the installed react-router version.
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: false });
    },
    [params, setParams]
  );

  const setStage = useCallback(
    (next: string | null) =>
      // Selecting, re-selecting, or clearing a stage always drops the selected PR so the panel can
      // fall back to the stage explanation (or the page help). Both mutations happen in one update
      // because each update() rebuilds from the current params, so two separate calls would clobber
      // each other.
      update(p => {
        if (next) p.set('stage', next);
        else p.delete('stage');
        p.delete('pr');
      }),
    [update]
  );

  const setPr = useCallback(
    (next: number | null) =>
      update(p => {
        if (next) p.set('pr', String(next));
        else p.delete('pr');
      }),
    [update]
  );

  const setAuthor = useCallback(
    (next: string) => {
      // Persist so the next visit defaults to the same author without needing it in the URL.
      if (typeof localStorage !== 'undefined') localStorage.setItem(AUTHOR_STORAGE_KEY, next);
      update(p => p.set('author', next));
    },
    [update]
  );

  return { stage, author, prNumber, setStage, setAuthor, setPr };
}
